import type { PointData } from '../types/companion'

/**
 * Matches the three POINT tag forms at the end of a response:
 *   [POINT:none]              → no point (groups 1-4 all undefined)
 *   [POINT:x,y:label]        → coordinate form (groups 1=x, 2=y, 3=label, 4=undefined)
 *   [POINT:<element-label>]  → label form (groups 1-3 undefined, 4=elementLabel)
 *
 * Ordering matters: coordinate form is tried before label form so that
 * [POINT:1,2:my-label] is always parsed as coordinates, never as a label.
 */
const POINT_REGEX = /\[POINT:(?:none|(\d+)\s*,\s*(\d+):([^\]]+)|([^\]]+))\]\s*$/

/**
 * A POINT tag anywhere in the text, with the spaces before it. Only the
 * trailing tag points; one the model leaves mid-reply (inside a list of steps,
 * say) would otherwise reach the chat as raw text.
 */
const STRAY_POINT_REGEX = /[ \t]*\[POINT:[^\]]*\]/g

/**
 * Parses a POINT tag from the end of Claude's response.
 * Returns the parsed point data and the text with every POINT tag stripped.
 *
 * Supported forms:
 *   [POINT:none]                   → null point
 *   [POINT:x,y:label]              → coordinate-addressed point (x, y in capture space)
 *   [POINT:<element-label>]        → label-addressed point (resolved client-side from DOM)
 */
export function parsePointTag(text: string): {
  point: PointData | null
  cleanText: string
} {
  const match = text.match(POINT_REGEX)

  if (!match) {
    return { point: null, cleanText: text.replace(STRAY_POINT_REGEX, '') }
  }

  const [fullMatch, xStr, yStr, coordLabel, elementLabel] = match
  const cleanText = text
    .slice(0, text.length - fullMatch.length)
    .replace(STRAY_POINT_REGEX, '')
    .trimEnd()

  // [POINT:none]: the `none` alternative matched; no capture groups set
  if (!xStr && !yStr && !coordLabel && !elementLabel) {
    return { point: null, cleanText }
  }

  // Coordinate form: [POINT:x,y:label]
  if (xStr !== undefined && yStr !== undefined) {
    return {
      point: {
        x: parseInt(xStr, 10),
        y: parseInt(yStr, 10),
        label: coordLabel!.trim(),
      },
      cleanText,
    }
  }

  // Label form: [POINT:<element-label>]
  // x/y are set to 0 as sentinels; CompanionOverlay resolves them from the DOM
  // before the point ever reaches BuddyCursor.
  const resolvedLabel = elementLabel!.trim()
  return {
    point: {
      x: 0,
      y: 0,
      label: resolvedLabel,
      elementLabel: resolvedLabel,
    },
    cleanText,
  }
}
